import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Bot, CheckCircle2, XCircle, Clock, Zap, Package, Users,
  Search, MessageSquare, DollarSign, TrendingUp, BarChart3,
  Play, Loader2, AlertTriangle, Lightbulb, ArrowRight, RefreshCw,
  Globe, Link2, Building2, MapPin, UserPlus, Radar,
  Compass, Landmark, PenTool, Wallet
} from 'lucide-react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function getHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const ACTION_ICONS = {
  research_suppliers: Search,
  contact_contractor: Users,
  propose_deal: DollarSign,
  update_pricing: TrendingUp,
  create_negotiation: MessageSquare,
  add_to_catalog: Package,
  market_analysis: BarChart3,
  followup_supplier: RefreshCw,
  search_companies: Globe,
  match_investment: Link2,
  search_project_roles: Building2,
};

const ACTION_LABELS = {
  research_suppliers: { pl: 'Szukaj dostawców', en: 'Find Suppliers' },
  contact_contractor: { pl: 'Kontakt z kontrahentem', en: 'Contact Contractor' },
  propose_deal: { pl: 'Zaproponuj deal', en: 'Propose Deal' },
  update_pricing: { pl: 'Aktualizuj ceny', en: 'Update Pricing' },
  create_negotiation: { pl: 'Utwórz negocjację', en: 'Create Negotiation' },
  add_to_catalog: { pl: 'Dodaj do katalogu', en: 'Add to Catalog' },
  market_analysis: { pl: 'Analiza rynku', en: 'Market Analysis' },
  followup_supplier: { pl: 'Follow-up', en: 'Follow-up' },
  search_companies: { pl: 'Szukaj firm', en: 'Search Companies' },
  match_investment: { pl: 'Dopasuj do inwestycji', en: 'Match Investment' },
  search_project_roles: { pl: 'Szukaj ról projektowych', en: 'Search Project Roles' },
};

const STATUS_CONFIG = {
  pending_approval: { label: { pl: 'Oczekuje', en: 'Pending' }, color: 'bg-amber-400/10 text-amber-400', icon: Clock },
  approved: { label: { pl: 'Zatwierdzone', en: 'Approved' }, color: 'bg-green-400/10 text-green-400', icon: CheckCircle2 },
  rejected: { label: { pl: 'Odrzucone', en: 'Rejected' }, color: 'bg-red-400/10 text-red-400', icon: XCircle },
  executed: { label: { pl: 'Wykonane', en: 'Executed' }, color: 'bg-blue-400/10 text-blue-400', icon: Zap },
  failed: { label: { pl: 'Błąd', en: 'Failed' }, color: 'bg-red-400/10 text-red-400', icon: AlertTriangle },
};

const PRIORITY_COLORS = {
  low: 'bg-blue-400/10 text-blue-400',
  medium: 'bg-amber-400/10 text-amber-400',
  high: 'bg-orange-400/10 text-orange-400',
  critical: 'bg-red-400/10 text-red-400',
};

export default function MerchantPipeline({ lang, tenantId }) {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState('');
  const [filter, setFilter] = useState('all');
  const [decidingId, setDecidingId] = useState(null);
  // Active Search & Investment Matching state
  const [activeTab, setActiveTab] = useState('pipeline'); // pipeline | search | matching
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStrategy, setSearchStrategy] = useState('industry_vertical');
  const [matching, setMatching] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [importingId, setImportingId] = useState(null);
  const [importedIds, setImportedIds] = useState(new Set()); // Track imported companies
  const [executingMatchId, setExecutingMatchId] = useState(null);
  const [executedMatchIds, setExecutedMatchIds] = useState(new Set()); // Track executed matches
  const [searchPage, setSearchPage] = useState(0); // Pagination
  const SEARCH_PAGE_SIZE = 10;

  useEffect(() => { loadData(); }, [tenantId]);

  async function loadData() {
    setLoading(true);
    try {
      const headers = getHeaders();
      const [q, s] = await Promise.all([
        axios.get(`${API_BASE}/merchant/queue`, { params: { tenant_id: tenantId }, headers }).then(r => r.data),
        axios.get(`${API_BASE}/merchant/stats`, { params: { tenant_id: tenantId }, headers }).then(r => r.data)
      ]);
      setQueue(q);
      setStats(s);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleAnalyze() {
    setAnalyzing(true);
    setAnalysis('');
    try {
      const headers = getHeaders();
      const result = await axios.post(`${API_BASE}/merchant/analyze`, {}, {
        params: { tenant_id: tenantId }, headers
      }).then(r => r.data);
      setAnalysis(result.analysis || '');
      toast.success(lang === 'pl'
        ? `Agent zaproponował ${result.count} akcji`
        : `Agent proposed ${result.count} actions`);
      loadData();
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd analizy' : 'Analysis failed');
    }
    setAnalyzing(false);
  }

  async function handleDecide(actionId, approved) {
    setDecidingId(actionId);
    try {
      const headers = getHeaders();
      await axios.put(`${API_BASE}/merchant/queue/${actionId}/decide`, null, {
        params: { approved }, headers
      });
      toast.success(approved
        ? (lang === 'pl' ? 'Zatwierdzono i wykonano' : 'Approved and executed')
        : (lang === 'pl' ? 'Odrzucono' : 'Rejected'));
      loadData();
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd operacji' : 'Operation failed');
    }
    setDecidingId(null);
  }

  async function handleSearch(auto = false, overrideStrategy = null, overrideQuery = null) {
    setSearching(true);
    setSearchResults(null);
    setImportedIds(new Set());
    setSearchPage(0);
    try {
      const headers = getHeaders();
      let result;
      if (auto) {
        result = await axios.post(`${API_BASE}/company-search/auto`, {}, {
          params: { tenant_id: tenantId }, headers
        }).then(r => r.data);
      } else if (overrideStrategy === 'architects') {
        result = await axios.post(`${API_BASE}/architects/search`, {
          query: overrideQuery || searchQuery || 'Biura architektoniczne',
          region: '',
        }, { params: { tenant_id: tenantId }, headers }).then(r => r.data);
      } else if (overrideStrategy === 'investors') {
        result = await axios.post(`${API_BASE}/investors/search`, {
          query: overrideQuery || searchQuery || 'Inwestorzy i deweloperzy',
          region: '',
        }, { params: { tenant_id: tenantId }, headers }).then(r => r.data);
      } else {
        result = await axios.post(`${API_BASE}/company-search`, {
          query: searchQuery,
          strategy: searchStrategy,
        }, { params: { tenant_id: tenantId }, headers }).then(r => r.data);
      }
      setSearchResults(result);
      const count = result.companies?.length || result.total_unique || 0;
      toast.success(lang === 'pl'
        ? `Znaleziono ${count} firm`
        : `Found ${count} companies`);
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd wyszukiwania' : 'Search failed');
    }
    setSearching(false);
  }

  async function handleMatchByRole(role) {
    setMatching(true);
    setMatchResults(null);
    setExecutedMatchIds(new Set());
    try {
      const headers = getHeaders();
      const endpoint = role === 'architect' ? '/architects/match' : '/investors/match';
      const result = await axios.post(`${API_BASE}${endpoint}`, {}, {
        params: { tenant_id: tenantId }, headers
      }).then(r => r.data);
      setMatchResults(result);
      setActiveTab('matching');
      toast.success(lang === 'pl'
        ? `Znaleziono ${result.matches?.length || 0} dopasowań`
        : `Found ${result.matches?.length || 0} matches`);
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd dopasowania' : 'Matching failed');
    }
    setMatching(false);
  }

  async function handleImportCompany(company, idx) {
    setImportingId(idx);
    try {
      const headers = getHeaders();
      await axios.post(`${API_BASE}/company-search/import`, company, {
        params: { tenant_id: tenantId }, headers
      });
      setImportedIds(prev => new Set([...prev, idx]));
      toast.success(lang === 'pl'
        ? `Zaimportowano: ${company.company_name}`
        : `Imported: ${company.company_name}`);
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd importu' : 'Import failed');
    }
    setImportingId(null);
  }

  async function handleInvestmentMatch() {
    setMatching(true);
    setMatchResults(null);
    setExecutedMatchIds(new Set());
    try {
      const headers = getHeaders();
      const result = await axios.post(`${API_BASE}/investment-match`, {}, {
        params: { tenant_id: tenantId }, headers
      }).then(r => r.data);
      setMatchResults(result);
      toast.success(lang === 'pl'
        ? `Znaleziono ${result.matches?.length || 0} dopasowań`
        : `Found ${result.matches?.length || 0} matches`);
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd dopasowania' : 'Matching failed');
    }
    setMatching(false);
  }

  async function handleExecuteMatch(match, idx) {
    setExecutingMatchId(idx);
    try {
      const headers = getHeaders();
      await axios.post(`${API_BASE}/investment-match/execute`, match, {
        params: { tenant_id: tenantId }, headers
      });
      setExecutedMatchIds(prev => new Set([...prev, idx]));
      toast.success(lang === 'pl'
        ? `Połączono: ${match.contractor_name} → ${match.project_name}`
        : `Connected: ${match.contractor_name} → ${match.project_name}`);
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd wykonania' : 'Execution failed');
    }
    setExecutingMatchId(null);
  }

  const filteredQueue = filter === 'all'
    ? queue
    : queue.filter(a => a.status === filter);

  const pendingCount = queue.filter(a => a.status === 'pending_approval').length;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Bot className="w-6 h-6 text-primary" />
            {lang === 'pl' ? 'Autonomiczny Handlowiec' : 'Autonomous Merchant'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === 'pl'
              ? 'Agent aktywnie szuka firm, dopasowuje do inwestycji i proponuje akcje'
              : 'Agent actively searches companies, matches investments, and proposes actions'}
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'pipeline' && (
            <Button onClick={handleAnalyze} disabled={analyzing} className="gap-2">
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {lang === 'pl' ? 'Analizuję...' : 'Analyzing...'}
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  {lang === 'pl' ? 'Uruchom analizę' : 'Run Analysis'}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex items-center gap-1 border-b border-border pb-0">
        {[
          { key: 'pipeline', label: { pl: 'Pipeline handlowy', en: 'Trade Pipeline' }, icon: Bot },
          { key: 'search', label: { pl: 'Szukaj firm', en: 'Search Companies' }, icon: Radar },
          { key: 'matching', label: { pl: 'Dopasuj do inwestycji', en: 'Match Investments' }, icon: Link2 },
        ].map(tab => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-1.5 ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <TabIcon className="w-4 h-4" />
              {tab.label[lang] || tab.label.en}
            </button>
          );
        })}
      </div>

      {/* ===== ACTIVE SEARCH TAB ===== */}
      {activeTab === 'search' && (
        <div className="space-y-4">
          {/* Quick Action Buttons — Architects & Investors */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <button
              onClick={() => handleSearch(false, 'architects', 'Biura architektoniczne i architekci')}
              disabled={searching}
              className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-purple-400/40 hover:bg-purple-400/5 transition-all text-left group"
            >
              <div className="w-10 h-10 rounded-xl bg-purple-400/10 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-400/20 transition-colors">
                <PenTool className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium">{lang === 'pl' ? 'Szukaj architektów' : 'Find Architects'}</p>
                <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Biura projektowe, architekci wnętrz' : 'Architecture firms, interior designers'}</p>
              </div>
            </button>
            <button
              onClick={() => handleSearch(false, 'investors', 'Inwestorzy, deweloperzy, fundusze')}
              disabled={searching}
              className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-green-400/40 hover:bg-green-400/5 transition-all text-left group"
            >
              <div className="w-10 h-10 rounded-xl bg-green-400/10 flex items-center justify-center flex-shrink-0 group-hover:bg-green-400/20 transition-colors">
                <Landmark className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium">{lang === 'pl' ? 'Szukaj inwestorów' : 'Find Investors'}</p>
                <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Deweloperzy, fundusze, inwestorzy' : 'Developers, funds, investors'}</p>
              </div>
            </button>
            <button
              onClick={() => handleMatchByRole('architect')}
              disabled={matching}
              className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-purple-400/40 hover:bg-purple-400/5 transition-all text-left group"
            >
              <div className="w-10 h-10 rounded-xl bg-purple-400/10 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-400/20 transition-colors">
                <Compass className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium">{lang === 'pl' ? 'Dopasuj architektów' : 'Match Architects'}</p>
                <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Połącz z projektami' : 'Connect to projects'}</p>
              </div>
            </button>
            <button
              onClick={() => handleMatchByRole('investor')}
              disabled={matching}
              className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-green-400/40 hover:bg-green-400/5 transition-all text-left group"
            >
              <div className="w-10 h-10 rounded-xl bg-green-400/10 flex items-center justify-center flex-shrink-0 group-hover:bg-green-400/20 transition-colors">
                <Wallet className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium">{lang === 'pl' ? 'Dopasuj inwestorów' : 'Match Investors'}</p>
                <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Połącz z projektami' : 'Connect to projects'}</p>
              </div>
            </button>
          </div>

          {/* Search Form */}
          <Card className="border border-border">
            <CardContent className="p-4 space-y-3">
              <div className="flex gap-3 items-end flex-wrap">
                <div className="flex-1 min-w-[250px]">
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {lang === 'pl' ? 'Czego szukasz?' : 'What are you looking for?'}
                  </label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder={lang === 'pl' ? 'np. dostawcy oświetlenia LED w Warszawie' : 'e.g. LED lighting suppliers in Warsaw'}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                  />
                </div>
                <div className="min-w-[180px]">
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {lang === 'pl' ? 'Strategia' : 'Strategy'}
                  </label>
                  <select
                    value={searchStrategy}
                    onChange={e => setSearchStrategy(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                  >
                    <option value="industry_vertical">{lang === 'pl' ? 'Branża / Specjalizacja' : 'Industry / Specialization'}</option>
                    <option value="architects">{lang === 'pl' ? 'Architekci / Biura projektowe' : 'Architects / Design Firms'}</option>
                    <option value="investors">{lang === 'pl' ? 'Inwestorzy / Deweloperzy / Fundusze' : 'Investors / Developers / Funds'}</option>
                    <option value="project_needs">{lang === 'pl' ? 'Potrzeby projektu' : 'Project Needs'}</option>
                    <option value="investment_partners">{lang === 'pl' ? 'Partnerzy inwestycyjni' : 'Investment Partners'}</option>
                    <option value="supply_chain">{lang === 'pl' ? 'Łańcuch dostaw' : 'Supply Chain'}</option>
                    <option value="geographic_expansion">{lang === 'pl' ? 'Ekspansja geograficzna' : 'Geographic Expansion'}</option>
                    <option value="competitor_mapping">{lang === 'pl' ? 'Mapowanie konkurencji' : 'Competitor Mapping'}</option>
                  </select>
                </div>
                <Button onClick={() => {
                  const s = searchStrategy;
                  if (s === 'architects' || s === 'investors') {
                    handleSearch(false, s, searchQuery);
                  } else {
                    handleSearch(false);
                  }
                }} disabled={searching || !searchQuery.trim()} className="gap-1.5">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {lang === 'pl' ? 'Szukaj' : 'Search'}
                </Button>
                <Button onClick={() => handleSearch(true)} disabled={searching} variant="secondary" className="gap-1.5">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />}
                  {lang === 'pl' ? 'Auto-szukaj' : 'Auto-search'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Search Results */}
          {searchResults && (
            <div className="space-y-3">
              {searchResults.search_meta && (
                <Card className="border border-primary/20 bg-primary/5">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-primary mb-1">
                      {lang === 'pl' ? 'Podsumowanie rynku' : 'Market Summary'}
                    </p>
                    <p className="text-sm">{searchResults.search_meta.market_summary}</p>
                    {searchResults.search_meta.recommended_next_searches?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Sugerowane kolejne wyszukiwania:' : 'Suggested next searches:'}</p>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {searchResults.search_meta.recommended_next_searches.map((s, i) => (
                            <button key={i} onClick={() => { setSearchQuery(s); }} className="text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-foreground">{s}</button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Searches done (for auto-search) */}
              {searchResults.searches_done && (
                <div className="flex gap-2 flex-wrap">
                  {searchResults.searches_done.map((s, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {s.strategy}: {s.found || 0} {lang === 'pl' ? 'firm' : 'companies'}
                      {s.project && ` (${s.project})`}
                    </Badge>
                  ))}
                </div>
              )}

              {(() => {
                const allCompanies = searchResults.companies || [];
                const totalPages = Math.ceil(allCompanies.length / SEARCH_PAGE_SIZE);
                const paged = allCompanies.slice(searchPage * SEARCH_PAGE_SIZE, (searchPage + 1) * SEARCH_PAGE_SIZE);
                return (<>
                {allCompanies.length > SEARCH_PAGE_SIZE && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{lang === 'pl' ? `Wyniki ${searchPage * SEARCH_PAGE_SIZE + 1}-${Math.min((searchPage + 1) * SEARCH_PAGE_SIZE, allCompanies.length)} z ${allCompanies.length}` : `Results ${searchPage * SEARCH_PAGE_SIZE + 1}-${Math.min((searchPage + 1) * SEARCH_PAGE_SIZE, allCompanies.length)} of ${allCompanies.length}`}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2" disabled={searchPage === 0} onClick={() => setSearchPage(p => p - 1)}>
                        {lang === 'pl' ? '← Poprzednie' : '← Previous'}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" disabled={searchPage >= totalPages - 1} onClick={() => setSearchPage(p => p + 1)}>
                        {lang === 'pl' ? 'Następne →' : 'Next →'}
                      </Button>
                    </div>
                  </div>
                )}
              {paged.map((company, localIdx) => {
                const idx = searchPage * SEARCH_PAGE_SIZE + localIdx;
                return (
                <Card key={idx} className="border border-border hover:border-primary/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="text-sm font-semibold">{company.company_name}</p>
                          {company.potential_role && (
                            <Badge variant="secondary" className="text-xs">{company.potential_role}</Badge>
                          )}
                          {company.cooperation_potential && (
                            <Badge variant="secondary" className={`text-xs ${
                              company.cooperation_potential >= 75 ? 'bg-green-400/10 text-green-400' :
                              company.cooperation_potential >= 50 ? 'bg-amber-400/10 text-amber-400' :
                              'bg-blue-400/10 text-blue-400'
                            }`}>
                              {company.cooperation_potential}%
                            </Badge>
                          )}
                        </div>
                        {company.contact_person && (
                          <p className="text-xs text-muted-foreground">{company.contact_person}</p>
                        )}
                        <p className="text-sm text-muted-foreground mt-1">{company.why_relevant}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground/60 flex-wrap">
                          {company.industry && <span>{company.industry}</span>}
                          {company.region && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{company.region}</span>}
                          {company.source && <span>({company.source})</span>}
                        </div>
                        {company.investment_fit && (
                          <div className="flex items-start gap-1.5 mt-2">
                            <Link2 className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-green-400">{company.investment_fit}</p>
                          </div>
                        )}
                        {company.suggested_approach && (
                          <div className="flex items-start gap-1.5 mt-1">
                            <Lightbulb className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-muted-foreground italic">{company.suggested_approach}</p>
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className={`gap-1.5 h-8 flex-shrink-0 ${importedIds.has(idx) ? 'bg-green-600 hover:bg-green-600' : ''}`}
                        onClick={() => handleImportCompany(company, idx)}
                        disabled={importingId === idx || importedIds.has(idx)}
                      >
                        {importingId === idx ? <Loader2 className="w-4 h-4 animate-spin" /> :
                         importedIds.has(idx) ? <CheckCircle2 className="w-4 h-4" /> :
                         <UserPlus className="w-4 h-4" />}
                        {importedIds.has(idx)
                          ? (lang === 'pl' ? 'Zaimportowano' : 'Imported')
                          : (lang === 'pl' ? 'Importuj' : 'Import')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
              })}
              </>);
              })()}
            </div>
          )}

          {!searchResults && !searching && (
            <Card className="border border-border">
              <CardContent className="py-16 text-center">
                <Radar className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-2">
                  {lang === 'pl'
                    ? 'Wyszukaj nowe firmy na rynku lub użyj auto-szukaj'
                    : 'Search for new companies or use auto-search'}
                </p>
                <p className="text-xs text-muted-foreground/60">
                  {lang === 'pl'
                    ? 'Auto-szukaj przeszuka rynek wg produktów, projektów, ICP i regionów'
                    : 'Auto-search will scan the market based on products, projects, ICP, and regions'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ===== INVESTMENT MATCHING TAB ===== */}
      {activeTab === 'matching' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-muted-foreground">
              {lang === 'pl'
                ? 'Automatyczne dopasowanie kontrahentów do projektów inwestycyjnych'
                : 'Automatic matching of contractors to investment projects'}
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => handleMatchByRole('architect')} disabled={matching} variant="secondary" className="gap-1.5" size="sm">
                <PenTool className="w-4 h-4 text-purple-400" />
                {lang === 'pl' ? 'Tylko architekci' : 'Architects only'}
              </Button>
              <Button onClick={() => handleMatchByRole('investor')} disabled={matching} variant="secondary" className="gap-1.5" size="sm">
                <Landmark className="w-4 h-4 text-green-400" />
                {lang === 'pl' ? 'Tylko inwestorzy' : 'Investors only'}
              </Button>
              <Button onClick={handleInvestmentMatch} disabled={matching} className="gap-2">
                {matching ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />{lang === 'pl' ? 'Dopasowuję...' : 'Matching...'}</>
                ) : (
                  <><Link2 className="w-4 h-4" />{lang === 'pl' ? 'Wszystkie dopasowania' : 'All Matches'}</>
                )}
              </Button>
            </div>
          </div>

          {matchResults && (
            <div className="space-y-3">
              {matchResults.summary && (
                <Card className="border border-primary/20 bg-primary/5">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-primary mb-1">{lang === 'pl' ? 'Podsumowanie' : 'Summary'}</p>
                    <p className="text-sm">{matchResults.summary}</p>
                  </CardContent>
                </Card>
              )}

              {matchResults.recommended_searches?.length > 0 && (
                <Card className="border border-amber-400/20 bg-amber-400/5">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-amber-400 mb-1">{lang === 'pl' ? 'Zalecane wyszukiwania' : 'Recommended Searches'}</p>
                    <div className="flex gap-1 flex-wrap mt-1">
                      {matchResults.recommended_searches.map((s, i) => (
                        <button key={i} onClick={() => { setSearchQuery(s); setActiveTab('search'); }}
                          className="text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-foreground">{s}</button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {(matchResults.matches || []).map((match, idx) => (
                <Card key={idx} className="border border-border hover:border-green-400/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-green-400/10 flex items-center justify-center flex-shrink-0">
                        <Link2 className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="text-sm font-semibold">
                            {match.contractor_name} <ArrowRight className="w-3 h-3 inline mx-1" /> {match.project_name}
                          </p>
                          <Badge variant="secondary" className="text-xs">{match.role}</Badge>
                          <Badge variant="secondary" className={`text-xs ${
                            match.match_score >= 75 ? 'bg-green-400/10 text-green-400' :
                            match.match_score >= 50 ? 'bg-amber-400/10 text-amber-400' :
                            'bg-blue-400/10 text-blue-400'
                          }`}>
                            {match.match_score}%
                          </Badge>
                          <Badge variant="secondary" className={`text-xs ${PRIORITY_COLORS[match.priority] || ''}`}>
                            {match.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{match.justification}</p>
                        {match.estimated_value > 0 && (
                          <p className="text-xs text-muted-foreground/60 mt-1 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> {match.estimated_value.toLocaleString()} PLN
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className={`gap-1.5 h-8 flex-shrink-0 ${executedMatchIds.has(idx) ? 'bg-green-600 hover:bg-green-600' : ''}`}
                        onClick={() => handleExecuteMatch(match, idx)}
                        disabled={executingMatchId === idx || executedMatchIds.has(idx)}
                      >
                        {executingMatchId === idx ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        {executedMatchIds.has(idx)
                          ? (lang === 'pl' ? 'Połączono' : 'Connected')
                          : (lang === 'pl' ? 'Połącz' : 'Connect')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {matchResults.unmatched_projects?.length > 0 && (
                <Card className="border border-red-400/20">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-red-400 mb-1">
                      {lang === 'pl' ? 'Projekty bez dopasowania' : 'Unmatched Projects'}
                    </p>
                    <p className="text-sm text-muted-foreground">{matchResults.unmatched_projects.join(', ')}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {!matchResults && !matching && (
            <Card className="border border-border">
              <CardContent className="py-16 text-center">
                <Link2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-2">
                  {lang === 'pl'
                    ? 'Kliknij "Znajdź dopasowania" aby połączyć kontrahentów z projektami'
                    : 'Click "Find Matches" to connect contractors with projects'}
                </p>
                <p className="text-xs text-muted-foreground/60">
                  {lang === 'pl'
                    ? 'System przeanalizuje role potrzebne w projektach i dopasuje kontrahentów'
                    : 'System will analyze roles needed in projects and match contractors'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ===== PIPELINE TAB ===== */}
      {activeTab === 'pipeline' && <>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Package className="w-5 h-5 mx-auto mb-1 text-primary" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{stats.products_in_catalog}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Produktów' : 'Products'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Clock className="w-5 h-5 mx-auto mb-1 text-amber-400" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{stats.pending}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Oczekuje' : 'Pending'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-green-400" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{stats.executed}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Wykonane' : 'Executed'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <XCircle className="w-5 h-5 mx-auto mb-1 text-red-400" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{stats.rejected}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Odrzucone' : 'Rejected'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Zap className="w-5 h-5 mx-auto mb-1 text-blue-400" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{stats.total_actions}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Łącznie' : 'Total'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Agent Analysis */}
      {analysis && (
        <Card className="border border-primary/20 bg-primary/5">
          <CardContent className="p-4 flex items-start gap-3">
            <Bot className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-primary mb-1">
                {lang === 'pl' ? 'Analiza agenta' : 'Agent Analysis'}
              </p>
              <p className="text-sm">{analysis}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'all', label: { pl: 'Wszystkie', en: 'All' }, count: queue.length },
          { key: 'pending_approval', label: { pl: 'Oczekujące', en: 'Pending' }, count: pendingCount },
          { key: 'executed', label: { pl: 'Wykonane', en: 'Executed' }, count: queue.filter(a => a.status === 'executed').length },
          { key: 'rejected', label: { pl: 'Odrzucone', en: 'Rejected' }, count: queue.filter(a => a.status === 'rejected').length },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === tab.key
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            {tab.label[lang] || tab.label.en}
            {tab.count > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-muted text-xs">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Action Queue */}
      {filteredQueue.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="py-16 text-center">
            <Bot className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              {lang === 'pl'
                ? 'Brak akcji w kolejce. Uruchom analizę, aby agent zaproponował działania.'
                : 'No actions in queue. Run analysis to have the agent propose actions.'}
            </p>
            <Button onClick={handleAnalyze} disabled={analyzing} variant="secondary" className="gap-1.5">
              <Play className="w-4 h-4" />
              {lang === 'pl' ? 'Uruchom analizę' : 'Run Analysis'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredQueue.map(action => {
            const ActionIcon = ACTION_ICONS[action.action_type] || Zap;
            const actionLabel = ACTION_LABELS[action.action_type] || { pl: action.action_type, en: action.action_type };
            const statusConfig = STATUS_CONFIG[action.status] || STATUS_CONFIG.pending_approval;
            const StatusIcon = statusConfig.icon;
            const isPending = action.status === 'pending_approval';
            const isDeciding = decidingId === action.id;

            return (
              <Card key={action.id} className={`border transition-colors ${
                isPending ? 'border-amber-400/30 bg-amber-400/5' : 'border-border'
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <ActionIcon className="w-5 h-5 text-primary" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-sm font-semibold">{action.title}</p>
                        <Badge variant="secondary" className="text-xs">
                          {actionLabel[lang] || actionLabel.en}
                        </Badge>
                        <Badge variant="secondary" className={`text-xs ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label[lang] || statusConfig.label.en}
                        </Badge>
                        <Badge variant="secondary" className={`text-xs ${PRIORITY_COLORS[action.priority] || ''}`}>
                          {action.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{action.description}</p>

                      {action.reasoning && (
                        <div className="flex items-start gap-1.5 mt-2">
                          <Lightbulb className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-muted-foreground italic">{action.reasoning}</p>
                        </div>
                      )}

                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground/60">
                        {action.product_name && (
                          <span className="flex items-center gap-1">
                            <Package className="w-3 h-3" /> {action.product_name}
                          </span>
                        )}
                        {action.target_name && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" /> {action.target_name}
                          </span>
                        )}
                        {action.estimated_value > 0 && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> {action.estimated_value.toLocaleString()} PLN
                          </span>
                        )}
                      </div>

                      {/* Execution result */}
                      {action.result && action.result.details && (
                        <div className="mt-2 p-2 rounded-lg bg-muted/30 text-xs">
                          <p className="font-medium text-green-400">{lang === 'pl' ? 'Wynik:' : 'Result:'}</p>
                          <p>{action.result.details}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {isPending && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:bg-red-400/10 h-8 px-3"
                          onClick={() => handleDecide(action.id, false)}
                          disabled={isDeciding}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          {lang === 'pl' ? 'Odrzuć' : 'Reject'}
                        </Button>
                        <Button
                          size="sm"
                          className="gap-1.5 h-8"
                          onClick={() => handleDecide(action.id, true)}
                          disabled={isDeciding}
                        >
                          {isDeciding ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4" />
                          )}
                          {lang === 'pl' ? 'Zatwierdź' : 'Approve'}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      </>}
    </div>
  );
}
